import { usePrevious, useUpdateEffect, useView } from '@10000/react'
import { useStack } from '../layout'
import { CardDetails, Subscriptions } from './payment'
import { Company, Industry } from './account'
import { OrganisationCompany, OrganisationIndustry } from './partner'
import { TeamLeader, TeamLevel, TeamName, TeamRole, Teams } from './team'
import { TeamLeadName } from './teamLead'
import { isFree, isPartner } from '../../lookups'
import { PasswordSetUp } from './PasswordSetUp.jsx'

// this component exists purely to reconstruct the onboarding screen stack after logging in
export const Onboarding = () => {
  const { replaceStack } = useStack()
  const {
    authenticated, onboard, free, admin, passwordSet, subscription, paymentMethodExists, companyDetailsSet, companyIndustry,
    partner, organisationsComplete, organisationDetailsSet, organisationIndustrySet, organisationId, teamsComplete,
    teamDetailsSet, teamLeaderSet, teamId, teamLevel, teamRole, isTeamLeader, teamOrganisation, userName
  } = useControllerData()
  const previouslyAuthenticated = usePrevious(authenticated)

  useUpdateEffect(() => {
    if(authenticated && !previouslyAuthenticated && !onboard && passwordSet) {
      const stack = getStack({
        admin, free, subscription, paymentMethodExists, companyDetailsSet, companyIndustry, partner, organisationsComplete,
        organisationDetailsSet, organisationIndustrySet, organisationId, teamsComplete, teamDetailsSet, teamLeaderSet,
        teamId, teamLevel, teamRole, isTeamLeader, teamOrganisation, userName, passwordSet
      })

      if(stack) {
        replaceStack(stack)
      }
    }
  }, [
    replaceStack, authenticated, previouslyAuthenticated, onboard, admin, free, passwordSet, subscription, paymentMethodExists,
    companyDetailsSet, companyIndustry, teamsComplete, partner, organisationsComplete, teamDetailsSet, organisationDetailsSet,
    organisationIndustrySet, organisationId, teamLeaderSet, teamId, teamLevel, teamRole, isTeamLeader, teamOrganisation, userName
  ])

  return null
}

// this hook is used in Router to determine the initial stack of onboarding components
export const useOnboardingStack = () => {
  const controllerData = useControllerData()
  if(controllerData.authenticated && !controllerData.onboard && (!controllerData.free || controllerData.signupStarted)) {
    return getStack(controllerData)
  }
}

const useControllerData = () => {
  const { user, subscription, paymentMethod, company, teams = [], organisations = [] } = useView('tenant')
  const authenticated = !!user?.id
  const { admin, onboard, passwordSet } = user || {}
  const paymentMethodExists = !!paymentMethod
  const partner = isPartner(subscription)
  const free = isFree(subscription)
  const signupStarted = subscription?.signupStarted
  const userName = user?.name

  const companyDetailsSet = company?.name && company?.country && company?.city
  const companyIndustry = company?.industry
  const teamIndex = teams?.findIndex(x => !teamIsOnboarded(x, user?.id))
  const teamsComplete = teamIndex === -1
  const teamDetailsSet = teams[teamIndex]?.name && teams[teamIndex]?.country && teams[teamIndex]?.city
  const teamLeaderSet = teams[teamIndex]?.leaders?.length > 0
  const { id: teamId, level: teamLevel, role: teamRole, leaders, organisation: teamOrganisation } = teams[teamIndex] || {}
  const isTeamLeader = leaders?.includes(user?.id)

  const organisationIndex = organisations.findIndex(x => !organisationIsComplete(x))
  const organisationsComplete = organisations.length > 0 && organisationIndex === -1
  const organisationDetailsSet = organisations[organisationIndex]?.name && organisations[organisationIndex]?.country &&
    organisations[organisationIndex]?.city
  const organisationIndustrySet = organisations[organisationIndex]?.industry !== undefined
  const { id: organisationId } = organisations[organisationsComplete ? organisations.findIndex(x => organisationIsComplete(x)) : organisationIndex] || {}

  return {
    authenticated, onboard, free, admin, passwordSet, subscription: subscription?.index, signupStarted, userName,
    paymentMethodExists, companyDetailsSet, companyIndustry, partner,
    organisationsComplete, organisationDetailsSet, organisationIndustrySet, organisationId,
    teamsComplete, teamDetailsSet, teamLeaderSet, teamId, teamLevel, teamRole, isTeamLeader, teamOrganisation
  }
}

const getStack = ({
  admin, free, subscription, paymentMethodExists, companyDetailsSet, companyIndustry, partner, organisationsComplete,
  organisationDetailsSet, organisationIndustrySet, organisationId, teamsComplete, teamDetailsSet, teamLeaderSet, teamId,
  teamLevel, teamRole, isTeamLeader, teamOrganisation, userName, passwordSet
}) => {
  if(!passwordSet && !free) {
    return [{ Component: PasswordSetUp }]
  }

  return currentStackStep(
    admin
      ? !paymentMethodExists
        ? [
            [{ Component: Subscriptions, props: { fromFreeSquiz: free } }, subscription === undefined || isFree({ index: subscription })],
            [{ Component: Company, props: { fromFreeSquiz: free } }, !companyDetailsSet],
            [{ Component: CardDetails, props: { fromFreeSquiz: free } }, !paymentMethodExists]
          ]
        : [
            ...(!passwordSet ? [[{ Component: PasswordSetUp, props: { fromFreeSquiz: free } }, !passwordSet]] : []),
            [{ Component: Industry }, companyIndustry === undefined],
            ...(partner
              ? [
                  [{ Component: OrganisationCompany, props: { organisationId } }, !organisationDetailsSet && !organisationsComplete],
                  [{ Component: OrganisationIndustry, props: { organisationId } }, !organisationIndustrySet && !organisationsComplete]
                ]
              : []
            ),
            [{ Component: Teams, props: { organisationId } }, teamsComplete],
            [{ Component: TeamLeader, props: { id: teamId, organisationId: teamOrganisation } }, !teamLeaderSet],
            [{ Component: TeamName, props: { id: teamId, organisationId: teamOrganisation } }, !teamDetailsSet && isTeamLeader],
            [{ Component: TeamLevel, props: { id: teamId, organisationId: teamOrganisation } }, teamLevel === undefined && isTeamLeader],
            [{ Component: TeamRole, props: { id: teamId, organisationId: teamOrganisation } }, teamRole === undefined && isTeamLeader]
          ]
      : [
          [{ Component: TeamLeadName }, !userName],
          [{ Component: Teams }, teamsComplete],
          [{ Component: TeamName, props: { id: teamId } }, !teamDetailsSet],
          [{ Component: TeamLevel, props: { id: teamId } }, teamLevel === undefined],
          [{ Component: TeamRole, props: { id: teamId } }, teamRole === undefined]
        ]
  )
}

const currentStackStep = stack => {
  const stackLength = stack.findIndex(([, test]) => test) + 1
  return stackLength > 0 && stack.slice(0, stackLength).map(([component]) => component)
}

export const teamIsOnboarded = ({ name, country, city, level, role, leaders } = {}, userId) => Boolean(
  (leaders?.length > 0 && !leaders.includes(userId)) ||
  teamIsComplete({ name, country, city, level, role, leaders })
)

export const teamIsComplete = ({ name, country, city, level, role, leaders } = {}) => Boolean(
  name && country && city && level !== undefined && role !== undefined && leaders?.length > 0
)
export const organisationIsComplete = ({ name, country, city, industry }) => Boolean(
  name && country && city && industry !== undefined
)